import type { EmailStatus } from '~/domains/messaging/types'
import type { OrderHead } from '~/domains/orders/types'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import type { ApplicantHeadData } from '../applicants/types'
import type { CompanyHead } from '../companies/types'
import type { ContactHeadResponse } from '../contacts/types'
import type { UserHeadResponse } from '../users/types'

export enum ActionType {
  CALL = 2,
  MEETING = 10,
  MEETING_CONTACT = 4,
  TASK = 25
}

export type ActionTypeHead = {
  id: number
  name: string
  expectsCompletion: boolean
  color?: string | null
  icon?: string | null
}

export type ActionHistoryEventHead = {
  type: string
  created: string
  scheduledDate?: string
  completionDate?: string
  isCancelled?: boolean
  completedBy?: UserHeadResponse | string
}

// Base type with common properties
type BaseApplicantHistoryEvent = ActionHistoryEventHead & {
  id: number
  createdBy?: string
  order?: OrderHead
  assignedUser?: string
  isCompleted?: boolean
  isCampaign?: boolean
}

export type CallScheduleEvent = BaseApplicantHistoryEvent & {
  type: 'recall' | 'recall_schedule'
  applicants?: string[]
  notes?: string
  actionType?: string
}

export type MeetingApplicantEvent = BaseApplicantHistoryEvent & {
  type: 'meeting_applicant' | 'meeting_contact' | 'meeting_schedule'
  applicants?: string[]
  contacts?: string[]
  notes?: string
  actionType?: string
  meetingLink?: string
}

export type SendEmailEvent = BaseApplicantHistoryEvent & {
  type: 'send_email' | 'email_received'
  emails?: string[]
  applicants?: string[]
  contacts?: string[]
  notes?: string
  subject?: string
  excerpt?: string
  content?: string
  emailAttachments?: { filename: string | null; url: string }[] | null
  emailInlineImages?: { contentId: string; url: string }[] | null
  emailStatus?: EmailStatus
  isAutoReply?: boolean
}

export type CreateOrderEvent = BaseApplicantHistoryEvent & {
  type: 'create_order'
}

export type TestSentEvent = BaseApplicantHistoryEvent & {
  type: 'test_sent'
  applicants?: string[]
}

export type TestCompletedEvent = BaseApplicantHistoryEvent & {
  type: 'test_completed'
  applicants?: string[]
  /** Name of the evaluation, as reported by the test provider. */
  notes?: string
}

export type ShortlistApplicantFeedbackData = {
  rank: number | null
  total: number
  accepted: boolean | null
  comment: string | null
}

export type ShortlistSubmittedEvent = BaseApplicantHistoryEvent & {
  type: 'shortlist_submitted'
  applicants?: string[]
  contacts?: string[]
  shortlistId?: number | null
  shortlistFeedback?: ShortlistApplicantFeedbackData | null
}

export type ReceiveSmsEvent = BaseApplicantHistoryEvent & {
  type: 'sms_received'
  applicants?: string[]
  notes?: string
  smsContent?: string
  smsStatus?: string
}

export type SendSmsEvent = BaseApplicantHistoryEvent & {
  type: 'sms_sent'
  applicants?: string[]
  notes?: string
  smsContent?: string
  smsStatus?: string
}

export type StatusUpdateEvent = BaseApplicantHistoryEvent & {
  type: 'status_update'
  applicants?: string[]
  contacts?: string[]
  status?: TaxonomyItem<'statuses'>
  orderStatus?: TaxonomyItem<'applicant_order_statuses'>
  archiveType?: TaxonomyItem<'archive_types'>
  notes?: string
}

export type NotesEvent = BaseApplicantHistoryEvent & {
  type: 'notes'
  applicants?: string[]
  notes?: string
  actionType?: string
}

export type AvailabilityUpdateEvent = BaseApplicantHistoryEvent & {
  type: 'availability_update'
  applicant?: string
  scheduleDate?: string
}

export type TaskEvent = BaseApplicantHistoryEvent & {
  type: 'task'
  applicants?: string[]
  contacts?: string[]
  subject?: string
  notes?: string
  actionType?: string
}

export type ApplicantHistoryEvent =
  | CallScheduleEvent
  | MeetingApplicantEvent
  | SendEmailEvent
  | ReceiveSmsEvent
  | SendSmsEvent
  | StatusUpdateEvent
  | NotesEvent
  | AvailabilityUpdateEvent
  | CreateOrderEvent
  | TaskEvent
  | TestSentEvent
  | TestCompletedEvent
  | ShortlistSubmittedEvent

export const filterableActionTypes = [
  { label: 'Appel', value: 'recall' },
  { label: 'Rendez-vous', value: 'meeting_applicant' },
  { label: 'Email envoyé', value: 'send_email' },
  { label: 'Email reçu', value: 'email_received' },
  { label: 'SMS reçu', value: 'sms_received' },
  { label: 'SMS envoyé', value: 'sms_sent' },
  { label: 'Statut changé', value: 'status_update' },
  { label: 'Note prise', value: 'notes' },
  { label: 'Tâche', value: 'task' },
  { label: 'Disponibilité mise à jour', value: 'availability_update' },
  { label: 'Test envoyé', value: 'test_sent' },
  { label: 'Test complété', value: 'test_completed' },
  { label: 'Retour de présentation client', value: 'shortlist_submitted' }
]

export type Action = {
  id: number
  type: ActionType
  typeName: string
  actionType?: ActionTypeHead | null
  expectsCompletion: boolean
  user: UserHeadResponse | null
  createdBy?: UserHeadResponse | null
  datetime: string | null
  completionDate?: string
  completedBy?: UserHeadResponse | string | null
  eventDate: string
  scheduledDateEnd?: string
  notes?: string
  created: string
  applicants: ApplicantHeadData[]
  contacts: (ContactHeadResponse & { company: CompanyHead })[]
  users?: UserHeadResponse[]
  externalParticipants?: { email: string; name: string | null }[]
  isCompleted?: boolean | null
  isCancelled?: boolean
  isPrivate?: boolean
  resolvedAddress?: string
  subject?: string | null
  meetingLink?: string | null
}

export type AddressBookType = 'user' | 'applicant' | 'contact' | 'external'

export type AddressBookHit = {
  uid: string
  id: number
  type: AddressBookType
  fullname?: string
  firstname?: string
  lastname?: string
  email: string
  company?: string
  company_id?: number
}

export type TypedAddress = {
  /** The raw value to submit (kept as "Name <email>" so the backend captures the name). */
  raw: string
  name?: string
  email: string
}

export type RecipientType = 'applicant' | 'contact'

export type RecipientIds = {
  applicantIds: number[]
  contactIds: number[]
}

export type RecipientDisplayItem = {
  id: number
  type: RecipientType
  label: string
  href?: string
  secondaryText?: string
  removable?: boolean
}
